// src/pages/ApplicationChampionshipSeason/shared/steps/Step2Stages/KnockoutStageItem.tsx
import { FunctionComponent } from 'react';
import { Box, Flex, IconButton, Input, Text } from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { StageType } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { SeasonFormData } from '../../schemas';
import { StageTypeBadge } from '../../components/StageTypeBadge';

type KnockoutStageItemProps = {
  id: string;
  index: number;
  onRemove: () => void;
  /** Quando true, esconde drag-handle e lixeira (mas mantém o input do nome). */
  isReadOnly?: boolean;
};

export const KnockoutStageItem: FunctionComponent<KnockoutStageItemProps> = ({
  id,
  index,
  onRemove,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
  } = useFormContext<SeasonFormData>();
  const dragControls = useDragControls();

  const error = errors.knockout_stages?.[index]?.name?.message;

  return (
    <Reorder.Item
      as="div"
      value={id}
      dragListener={false}
      dragControls={dragControls}
    >
      <Box
        bg="white"
        borderWidth="1px"
        borderColor={error ? 'red.400' : 'gray.200'}
        borderRadius="md"
        px={3}
        py={2}
      >
        <Flex align="center" gap={2}>
          {isReadOnly ? null : (
            <Flex
              align="center"
              justify="center"
              flexShrink={0}
              w="24px"
              color="gray.400"
              cursor="grab"
              style={{ touchAction: 'none' }}
              onPointerDown={event => dragControls.start(event)}
              aria-label={t('pages.application_season.components.season_step2.actions.reorder_stage')}
            >
              <ChakraFeatherIcon name="menu" boxSize="16px" />
            </Flex>
          )}

          <Text flexShrink={0} w="16px" textAlign="center" color="gray.400" fontSize="14px">
            {index + 1}
          </Text>

          <Input
            flex="1"
            variant="unstyled"
            fontWeight="medium"
            placeholder={t('pages.application_season.components.season_step2.actions.stage_name_placeholder')}
            {...register(`knockout_stages.${index}.name`)}
          />

          <StageTypeBadge type={StageType.KNOCKOUT} />

          {isReadOnly ? null : (
            <IconButton
              aria-label={t('pages.application_season.components.season_step2.actions.remove_stage')}
              icon={<ChakraFeatherIcon name="trash-2" boxSize="16px" />}
              variant="ghost"
              size="sm"
              color="gray.400"
              onClick={onRemove}
            />
          )}
        </Flex>

        {error ? (
          <Text fontSize="12px" color="red.500" pl="58px" pt={1}>
            {error}
          </Text>
        ) : null}
      </Box>
    </Reorder.Item>
  );
};
